import { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
} from '@mui/material';
import useGentian from '../../core/hooks/useGentian';
import { Res } from '../../resources';

const stripePromise = loadStripe(Res.api.stripe.PUBLISHABLE_KEY);

const Accounts = () => {
  const { data: accountData } = useGentian().useAccount();
  const { data: credit, refetch } = useGentian().useCreditByUserId(accountData?.data?.user_id as string);

  const [amount, setAmount] = useState('');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const { mutate: createCreditSession, isLoading: creatingSession } = useGentian().useCreditCheckoutSession();
  const { data: resellers } = useGentian().getResellers;
  const { data: paymentStatusData } = useGentian().usePaymentStatus(paymentSessionId);
  const maxAmount = credit?.amountUsed ?? 0;
  const amountNumber = Number(amount);
  const isAmountValid = Number.isFinite(amountNumber) && amountNumber > 0 && amountNumber <= maxAmount;

  // map userId → name
  const userMap = useMemo(() => {
    if (!resellers) return {};
    const map: Record<string, string> = {};
    resellers.forEach((r) => {
      map[r.user_id] = r.name || r.user_metadata?.name || r.user_id;
    });
    return map;
  }, [resellers]);

  const handlePay = () => {
    if (!accountData?.data?.user_id || typeof accountData.data.user_id !== 'string') return;
    if (!isAmountValid) return;

    createCreditSession(
      { amount: amountNumber, userId: accountData?.data?.user_id },
      {
        onSuccess: (data) => {
          if (data?.clientSecret) setClientSecret(data.clientSecret);
          if (data?.sessionId) setPaymentSessionId(data.sessionId);
        },
        onError: (err) => {
          console.error('Failed to create credit checkout session', err);
        },
      },
    );
  };

  const handleBack = () => {
    setClientSecret(null);
    setPaymentSessionId('');
    setAmount('');
    refetch();
  };

  useEffect(() => {
    if (location.search.includes('payment=success')) {
      refetch();
    }
  }, [refetch]);

  useEffect(() => {
    if (paymentStatusData?.status === 'complete' && paymentStatusData?.paymentStatus === 'paid') {
      refetch();
    }
  }, [paymentStatusData?.paymentStatus, paymentStatusData?.status, refetch]);

  if (clientSecret) {
    return (
      <Box sx={{ minHeight: 600, p: 4 }}>
        <Button variant="outlined" sx={{ mb: 3 }} onClick={handleBack}>
          ← Back to Account
        </Button>
        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6, px: 2 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Account
      </Typography>

      {/* Outstanding balance */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: '#f9f9f9', borderRadius: 2, boxShadow: 1 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1" color="text.secondary">
            Outstanding Balance
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={credit?.amountUsed ? 'error.main' : 'success.main'}
          >
            £{(credit?.amountUsed ?? 0).toFixed(2)}
          </Typography>

          <TextField
            label="Payment amount (£)"
            type="number"
            value={amount}
            inputProps={{ min: 1, max: credit?.amountUsed }}
            onChange={(e) => {
              const next = e.target.value.replace(/^0+(?=\d)/, '');
              setAmount(next);
            }}
            fullWidth
          />

          <Button
            variant="contained"
            size="large"
            disabled={!isAmountValid || creatingSession}
            onClick={handlePay}
          >
            Pay £{Number.isFinite(amountNumber) && amountNumber > 0 ? amountNumber : 0}
          </Button>
        </Stack>
      </Paper>

      {/* Transaction Log */}
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Transaction Log
      </Typography>
      {credit?.transactionLog && credit.transactionLog.length > 0 ? (
        <List>
          {credit.transactionLog
            .slice()
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) // newest first
            .map((tx, index) => (
              <Paper key={index} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 1 }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {tx.type.toUpperCase()} - £{tx.amount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    By: {userMap[tx.createdBy] || tx.createdBy} | Date:{' '}
                    {new Date(tx.createdAt).toLocaleString()}
                  </Typography>
                  {tx.note && (
                    <Typography variant="body2" color="text.secondary">
                      Note: {tx.note}
                    </Typography>
                  )}
                  <Divider sx={{ mt: 1 }} />
                </Stack>
              </Paper>
            ))}
        </List>
      ) : (
        <Typography color="text.secondary">No transactions yet.</Typography>
      )}
    </Box>
  );
};

export default Accounts;
